import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useConvex, useMutation, useQuery } from "convex/react";
import { useAuthActions } from "@convex-dev/auth/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { Mail, Github, AlertCircle, CheckCircle } from "lucide-react";
import { api } from "@/lib/convex-api";
import { projectRoute } from "@/lib/app-routes";
import { formatUserFacingError } from "@/lib/errorMessages";
import { organizationContext } from "@/lib/organization-context";

const inviteSchema = z.object({
  email: z.string().email(),
});

type InviteFormValues = z.infer<typeof inviteSchema>;

const GoogleIcon = () => (
  <svg className="size-4" viewBox="0 0 24 24" fill="none">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

export const InvitePage = () => {
  const convex = useConvex();
  const navigate = useNavigate();
  const { token } = useParams({ from: "/invite/$token" });
  const invite = useQuery(
    api.notifications.getInviteByToken,
    token?.trim() ? { token } : "skip",
  );
  const isValid = Boolean(token?.trim()) && Boolean(invite?.isUsable);
  const { signIn } = useAuthActions();
  const me = useQuery(api.users.me);
  const acceptInvite = useMutation(api.notifications.acceptInvite);
  const rejectInvite = useMutation(api.notifications.rejectInvite);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  const [tokenError, setTokenError] = useState("");
  const [isSubmittingAction, setIsSubmittingAction] = useState<
    "accept" | "reject" | null
  >(null);

  const form = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: "" },
  });

  const onSubmitEmail = async (data: InviteFormValues) => {
    if (!token || !invite) return;
    setSentEmail(data.email);
    setMagicLinkSent(true);
    const formData = new FormData();
    formData.set("email", data.email);
    formData.set("redirectTo", `/app/invite/${token}`);
    await signIn("resend", formData);
  };

  const handleGoogleSignIn = async () => {
    void form;
    setTokenError("Google sign-in is not configured yet.");
  };

  const handleGitHubSignIn = async () => {
    void form;
    setTokenError("GitHub sign-in is not configured yet.");
  };

  const signedInEmailMatchesInvite =
    Boolean(me?.email) &&
    Boolean(invite?.email) &&
    me!.email.trim().toLowerCase() === invite!.email.trim().toLowerCase();

  const handleInviteAction = async (action: "accept" | "reject") => {
    if (!invite) return;
    setTokenError("");
    setIsSubmittingAction(action);
    try {
      const result =
        action === "accept"
          ? await acceptInvite({ inviteId: invite.id })
          : await rejectInvite({ inviteId: invite.id });

      if (action === "accept") {
        organizationContext.setSelectedOrganizationId(result.workspaceId);
        if (result.scopeType === "project" && result.projectId) {
          const project = await convex.query(api.projects.getById, {
            id: result.projectId,
          });
          if (!project?.organizationSlug || !project.slug) {
            throw new Error("Accepted project route details were unavailable.");
          }
          await navigate({
            to: projectRoute,
            params: {
              orgSlug: project.organizationSlug,
              id: project.slug,
            },
          });
          return;
        }
        await navigate({ to: "/" });
        return;
      }

      await navigate({ to: "/" });
    } catch (error) {
      setTokenError(
        formatUserFacingError(
          error,
          action === "accept"
            ? "Failed to accept invite."
            : "Failed to decline invite.",
        ),
      );
    } finally {
      setIsSubmittingAction(null);
    }
  };

  if (!isValid) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#08080a] px-4">
        <div className="w-full max-w-sm space-y-8">
          <div className="text-center">
            <Link to="/" className="app-logo inline-flex items-center">
              <span className="font-['Days_One'] text-2xl font-bold text-[#e8e6e3] tracking-tight">
                md
              </span>
              <span className="font-['Libre_Baskerville'] text-2xl font-normal text-[#e8e6e3] tracking-tight">
                sync
              </span>
            </Link>
          </div>
          <div className="rounded-xl border border-[#1e1e24] bg-[#141418] p-6 shadow-lg shadow-black/20">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="rounded-full bg-red-950/40 p-3">
                <AlertCircle className="size-5 text-red-400" />
              </div>
              <div className="space-y-1.5">
                <h2 className="text-sm font-medium text-[#e8e6e3]">
                  Invalid invite
                </h2>
                <p className="text-sm text-[#9a9a9a]">
                  This invite link is invalid, expired, or already used.
                </p>
              </div>
              <Button
                asChild
                variant="outline"
                size="sm"
                className="mt-2 border-[#1e1e24] bg-[#1a1a1f] text-[#e8e6e3] hover:bg-[#2a2a30] hover:border-[#2a2a30] hover:text-[#e8e6e3]"
              >
                <Link to="/login">Go to Sign In</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#08080a] px-4">
      <div className="w-full max-w-sm space-y-8">
        {/* Logo */}
        <div className="text-center">
          <Link to="/" className="app-logo inline-flex items-center">
            <span className="font-['Days_One'] text-2xl font-bold text-[#e8e6e3] tracking-tight">
              md
            </span>
            <span className="font-['Libre_Baskerville'] text-2xl font-normal text-[#e8e6e3] tracking-tight">
              sync
            </span>
          </Link>
        </div>

        {/* Invite banner */}
        <div className="rounded-lg border border-[#1e1e24] bg-[#111114] px-4 py-3 text-center text-sm text-[#9a9a9a]">
          You&apos;ve been invited to{" "}
          <span className="font-medium text-[#e8e6e3]">
            {invite?.scopeType === "project"
              ? (invite?.projectName ?? "a project")
              : (invite?.workspaceName ?? "a workspace")}
          </span>
        </div>

        {/* Card */}
        <div className="rounded-xl border border-[#1e1e24] bg-[#141418] p-6 shadow-lg shadow-black/20">
          <div className="mb-6 text-center space-y-1.5">
            <h1 className="text-lg font-semibold text-[#e8e6e3]">
              Accept your invite
            </h1>
            <p className="text-sm text-[#9a9a9a]">
              {me
                ? `Review your invite to ${
                    invite?.scopeType === "project"
                      ? (invite?.projectName ?? "this project")
                      : (invite?.workspaceName ?? "this workspace")
                  }.`
                : `Sign in to review your invite to ${
                    invite?.scopeType === "project"
                      ? (invite?.projectName ?? "this project")
                      : (invite?.workspaceName ?? "this workspace")
                  }.`}
            </p>
            {tokenError ? (
              <p className="text-sm text-red-400">{tokenError}</p>
            ) : null}
          </div>

          <div className="space-y-6">
            {me ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-[#1e1e24] bg-[#111114] px-4 py-3 text-sm text-[#9a9a9a]">
                  <p className="text-[#e8e6e3]">
                    Signed in as <span className="font-medium">{me.email}</span>
                  </p>
                  <p className="mt-1">
                    {invite?.invitedByName ?? "A teammate"} invited you as a{" "}
                    <span className="font-medium text-[#e8e6e3]">
                      {invite?.role}
                    </span>
                    .
                  </p>
                </div>

                {!signedInEmailMatchesInvite ? (
                  <div className="rounded-lg border border-red-900/60 bg-red-950/30 px-4 py-3 text-sm text-red-200">
                    This invite was sent to{" "}
                    <span className="font-medium">{invite?.email}</span>. Sign
                    in with that email to continue.
                  </div>
                ) : (
                  <div className="flex items-center justify-end gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="border-[#1e1e24] bg-[#1a1a1f] text-[#e8e6e3] hover:bg-[#2a2a30] hover:border-[#2a2a30] hover:text-[#e8e6e3]"
                      disabled={isSubmittingAction !== null}
                      onClick={() => void handleInviteAction("reject")}
                    >
                      {isSubmittingAction === "reject"
                        ? "Declining..."
                        : "Decline invite"}
                    </Button>
                    <Button
                      type="button"
                      className="bg-[#d4a843] font-semibold text-[#08080a] hover:bg-[#e8bc5a]"
                      disabled={isSubmittingAction !== null}
                      onClick={() => void handleInviteAction("accept")}
                    >
                      {isSubmittingAction === "accept"
                        ? "Accepting..."
                        : "Accept invite"}
                    </Button>
                  </div>
                )}
              </div>
            ) : null}

            {!me ? (
              <>
                {/* Social sign-in */}
                <div className="space-y-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-[#1e1e24] bg-[#1a1a1f] text-[#e8e6e3] hover:bg-[#2a2a30] hover:border-[#2a2a30] hover:text-[#e8e6e3]"
                    onClick={handleGoogleSignIn}
                  >
                    <GoogleIcon />
                    Continue with Google
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-[#1e1e24] bg-[#1a1a1f] text-[#e8e6e3] hover:bg-[#2a2a30] hover:border-[#2a2a30] hover:text-[#e8e6e3]"
                    onClick={handleGitHubSignIn}
                  >
                    <Github className="size-4" />
                    Continue with GitHub
                  </Button>
                </div>

                {/* Divider */}
                <div className="relative">
                  <Separator className="bg-[#1e1e24]" />
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#141418] px-3 text-xs text-[#5a5a5e]">
                    or
                  </span>
                </div>

                {/* Email magic link */}
                {magicLinkSent ? (
                  <div className="flex flex-col items-center gap-2 py-2 text-center">
                    <div className="rounded-full bg-emerald-950/50 p-2">
                      <CheckCircle className="size-5 text-emerald-400" />
                    </div>
                    <p className="text-sm text-[#e8e6e3]">Check your inbox</p>
                    <p className="text-xs text-[#9a9a9a]">
                      We sent a link to{" "}
                      <span className="font-medium">{sentEmail}</span>
                    </p>
                  </div>
                ) : (
                  <Form {...form}>
                    <form
                      onSubmit={form.handleSubmit(onSubmitEmail)}
                      className="space-y-3"
                    >
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-[#9a9a9a]">
                              Email
                            </FormLabel>
                            <FormControl>
                              <Input
                                type="email"
                                placeholder="you@company.com"
                                className="border-[#1e1e24] bg-[#111114] text-[#e8e6e3] placeholder:text-[#5a5a5e] focus-visible:ring-[#d4a843]/30"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="submit"
                        className="w-full bg-[#d4a843] font-semibold text-[#08080a] hover:bg-[#e8bc5a]"
                      >
                        <Mail className="size-4" />
                        Send Magic Link
                      </Button>
                    </form>
                  </Form>
                )}
              </>
            ) : null}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-[#5a5a5e]">
          By signing in, you agree to our{" "}
          <a
            href="/terms"
            className="underline underline-offset-4 hover:text-[#9a9a9a]"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="/privacy"
            className="underline underline-offset-4 hover:text-[#9a9a9a]"
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
};
